import React from 'react';
import { Box, Typography } from '@mui/material';
import { buildBreakdownRows, breakdownFooter } from '../utils/creditBreakdown';

/**
 * What the balance is made of, and when each part goes.
 *
 * ── THE LAYOUT, AND WHY IT CHANGED ──────────────────────────────────────────
 * Each row used to be `justifyContent: 'space-between'` inside a card that
 * spans the page. `space-between` pushes the two children to opposite edges and
 * dumps every spare pixel between them, so on a wide screen the label sat on
 * the far left and its number several hundred pixels away on the far right,
 * with nothing in between to carry the eye across. `gap` cannot fix that — it
 * is a MINIMUM separation, and `space-between` always exceeds it when there is
 * free space.
 *
 * A bounded grid fixes it properly: the label column takes what it needs, the
 * number sits immediately after it, and the whole block stops well short of the
 * card edge. The numbers stay right-aligned against each other with tabular
 * figures, so they remain comparable down the column.
 */

const TEXT = '#1A1A1A';
const TEXT_MUTED = '#8A8F85';
/**
 * The "these are going away" colour, already used in the activity table for a
 * credit decrease. Deliberately not the theme's `error.main` — this is a
 * deadline, not a failure — and not the theme's `warning.main`, which is a grey
 * in this app and would read as disabled.
 */
const EXPIRING = '#B45309';

//  is set only while a free trial is RUNNING. It relabels the plan
// row and replaces its stored expiry with the trial end — see creditBreakdown.js
// for why the stored date is wrong on a trial.
export default function CreditBreakdown({ lots, now, trialEndsOn = null }) {
  const rows = buildBreakdownRows(lots, now, { trialEndsOn });

  // Nothing to break down. The headline above already says the balance, so a
  // panel of zeroes would only repeat it less clearly.
  if (rows.length === 0) return null;

  // Null when no note belongs here — on a trial, whenever the rows hold only
  // one type of credit. See breakdownFooter.
  const footer = breakdownFooter(trialEndsOn, rows);

  return (
    <Box sx={{ mt: 1.5, borderTop: '1px solid #E5E7E0', pt: 1.5 }}>
      {/* Bounded, so the number never drifts to the far edge of the card. */}
      <Box sx={{ maxWidth: 420 }}>
        {rows.map((row) => (
          <Box
            key={row.key}
            sx={{
              display: 'grid',
              // Label takes what it needs; number and flag take theirs; the
              // slack sits to the RIGHT of all three rather than between them.
              gridTemplateColumns: '1fr auto auto',
              columnGap: 2,
              alignItems: 'baseline',
              py: 0.75,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: 13,
                  color: row.expiringSoon ? EXPIRING : TEXT,
                }}
              >
                {row.label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 11,
                  color: row.expiringSoon ? EXPIRING : TEXT_MUTED,
                }}
              >
                {row.expiresLabel ? `expiring ${row.expiresLabel}` : 'no expiry date'}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 700,
                fontSize: 16,
                color: row.expiringSoon ? EXPIRING : TEXT,
                fontVariantNumeric: 'tabular-nums',
                textAlign: 'right',
              }}
            >
              {row.remaining.toLocaleString()}
            </Typography>

            {/* To the RIGHT of the number, as asked. Rendered as its own column
                so it cannot push the figures out of alignment on the rows that
                do not have it. */}
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: 10,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: EXPIRING,
                whiteSpace: 'nowrap',
                // Reserves the column on every row, so adding the flag to one
                // row does not shift the numbers on the others.
                visibility: row.expiringSoon ? 'visible' : 'hidden',
              }}
              aria-hidden={!row.expiringSoon}
            >
              Expiring soon
            </Typography>
          </Box>
        ))}
      </Box>

      {/* OLD: rendered unconditionally — {breakdownFooter(trialEndsOn)} — so a
          trial account with one credit type got a note that only repeated the
          row above it. */}
      {footer && (
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif', fontSize: 11, color: TEXT_MUTED, mt: 1.25, maxWidth: 420,
          }}
        >
          {footer}
        </Typography>
      )}
    </Box>
  );
}
